import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LayoutShell from "@/components/ui/LayoutShell";
import AfrimedSearchBar from "@/components/ui/AfrimedSearchBar.jsx";
import Card from "@/components/ui/Card.jsx";
import TopBar from "@/sot/primitives/TopBar.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import supabase from "@/services/supabaseClient";
import PrescriptionPatientCell from "@/components/prescriptions/PrescriptionPatientCell.jsx";
import {
  countPrescriptionsLast30Days,
  listDoctorPrescriptions,
} from "@/services/doctorPrescriptions.js";

function resolveUiLocale(languageCode) {
  if (languageCode === "sn") return "sn-ZW";
  if (languageCode === "zu") return "zu-ZA";
  return languageCode || undefined;
}

const CUSTOM_LOCALE_LABELS = {
  sn: ["Nd", "Kk", "Km", "Kb", "Cv", "Ck", "Cg", "Ny", "Gu", "Gm", "Mb", "Zv"],
  zu: ["Jan", "Feb", "Mas", "Eph", "Mey", "Jun", "Jul", "Aga", "Sep", "Okt", "Nov", "Dis"],
};

function fmtShortDate(value, languageCode, localeCode) {
  const date = new Date(value || "");
  if (Number.isNaN(date.getTime())) return "—";

  try {
    const custom = CUSTOM_LOCALE_LABELS[languageCode];
    if (custom) {
      return `${String(date.getDate()).padStart(2, "0")} ${custom[date.getMonth()]} ${date.getFullYear()}`;
    }
    return new Intl.DateTimeFormat(localeCode, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  } catch {
    return "—";
  }
}

function RxStatusPill({ status }) {
  const { t } = useLanguage();
  const value = String(status || "draft").toLowerCase();
  const map = {
    draft: {
      tone: "bg-[rgba(91,107,134,0.12)] text-[rgb(var(--clr-on-surface-variant))]",
      label: t("doctorDashboard.prescriptions.status.draft", "Draft"),
    },
    issued: {
      tone: "bg-[rgba(0,189,196,0.12)] text-[#008A8F]",
      label: t("doctorDashboard.prescriptions.status.issued", "Issued"),
    },
    dispensed: {
      tone: "bg-[rgba(34,197,94,0.12)] text-[rgba(22,163,74,1)]",
      label: t("doctorDashboard.prescriptions.status.dispensed", "Dispensed"),
    },
    cancelled: {
      tone: "bg-[rgba(239,68,68,0.12)] text-[rgba(185,28,28,1)]",
      label: t("doctorDashboard.prescriptions.status.cancelled", "Cancelled"),
    },
    expired: {
      tone: "bg-[rgba(234,179,8,0.12)] text-[rgba(161,98,7,1)]",
      label: t("doctorDashboard.prescriptions.status.expired", "Expired"),
    },
  };
  const meta = map[value] || map.draft;

  return (
    <span
      className={[
        "inline-flex items-center h-7 px-3 rounded-full text-label-small leading-none",
        meta.tone,
      ].join(" ")}
    >
      {meta.label}
    </span>
  );
}

export default function DoctorRecentPrescriptionsPage() {
  const auth = useAuth() || {};
  const { t, languageCode } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const userId = String(auth?.user?.id || "").trim();
  const isPreviewDoctor = userId === "dev-user";
  const localeCode = React.useMemo(() => resolveUiLocale(languageCode), [languageCode]);

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [searchQuery, setSearchQuery] = React.useState("");

  React.useEffect(() => {
    let alive = true;

    async function load() {
      if (!userId) {
        if (alive) {
          setLoading(false);
          setError(t("doctorDashboard.errors.sessionUnavailable", "Session unavailable."));
        }
        return;
      }

       if (isPreviewDoctor) {
        if (alive) {
          setRows([]);
          setError("");
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setError("");

      try {
        const nextRows = await listDoctorPrescriptions(supabase, userId, { limit: 100 });
        if (!alive) return;
        setRows(nextRows);
      } catch (err) {
        if (!alive) return;
        setError(t("doctorDashboard.prescriptionsPage.errors.load", "Failed to load prescriptions."));
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();

    return () => {
      alive = false;
    };
  }, [isPreviewDoctor, userId, t]);

  const filteredRows = React.useMemo(() => {
    const query = String(searchQuery || "").trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((row) =>
      [row?.patientName, row?.patientSecondary, row?.rxNo]
        .map((value) => String(value || "").toLowerCase())
        .some((value) => value.includes(query))
    );
  }, [rows, searchQuery]);

  function openPrescription(row) {
    if (!row?.id) return;
    navigate(`/appointments/prescriptions/${row.id}`, {
      state: {
        actorRole: "doctor",
        returnTo: "/doctors/prescriptions",
        listReturnTo: location.state?.returnTo || "/doctor/dashboard",
      },
    });
  }

  return (
    <LayoutShell railClassName="w-full">
      <div className="w-full pb-10 md:pb-16">
        <TopBar
          title={t("doctorDashboard.prescriptionsPage.title", "Prescription history")}
          onBack={() => navigate(location.state?.returnTo || "/doctor/dashboard")}
        />

        <Card className="mt-6 overflow-hidden">
          <div className="flex flex-col gap-3 px-4 pt-4 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="text-title-medium text-on-surface">
                {t("doctorDashboard.prescriptionsPage.heading", "Prescription history")}
              </div>
              <div className="mt-1 text-body-medium text-on-surface-var">
                {t(
                  "doctorDashboard.prescriptionsPage.subtitle",
                  "Issued prescriptions tied to appointment-based care."
                )}
                {!loading && !error
                  ? ` ${t(
                      "doctorDashboard.prescriptionsPage.issuedPast30Days",
                      "{count} issued in the past 30 days.",
                      { count: countPrescriptionsLast30Days(rows) }
                    )}`
                  : ""}
              </div>
            </div>
            <AfrimedSearchBar
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={t("doctorDashboard.prescriptions.searchPlaceholder", "Search by Patient Id")}
              className="w-full md:w-[240px]"
              ariaLabel={t("doctorDashboard.prescriptions.searchAria", "Search recent prescriptions by patient id")}
            />
          </div>

          <div className="hidden md:block mt-4">
            <div className="grid grid-cols-12 px-4 py-3 bg-[rgba(var(--clr-on-surface),0.04)]">
              <div className="col-span-3 text-label-medium text-on-surface-var">{t("doctorDashboard.prescriptions.columns.patient", "Patient")}</div>
              <div className="col-span-3 text-label-medium text-on-surface-var">{t("doctorDashboard.patientsAppointments.columns.primaryCondition", "Primary condition")}</div>
              <div className="col-span-2 text-label-medium text-on-surface-var">{t("doctorDashboard.prescriptions.columns.issued", "Issued")}</div>
              <div className="col-span-2 text-label-medium text-on-surface-var">{t("doctorDashboard.prescriptions.columns.expires", "Expires")}</div>
              <div className="col-span-1 text-label-medium text-on-surface-var">{t("doctorDashboard.prescriptionsPage.columns.refill", "Refill")}</div>
              <div className="col-span-1 text-label-medium text-on-surface-var text-right">{t("doctorDashboard.prescriptions.columns.status", "Status")}</div>
            </div>

            {loading ? (
              <div className="px-4 py-4 text-body-medium text-on-surface-var">{t("doctorDashboard.prescriptions.loading", "Loading prescriptions...")}</div>
            ) : error ? (
              <div className="px-4 py-4 text-body-medium text-on-surface-var">{error}</div>
            ) : filteredRows.length === 0 ? (
              <div className="px-4 py-4 text-body-medium text-on-surface-var">
                {rows.length === 0
                  ? t("doctorDashboard.prescriptions.empty", "No prescriptions yet.")
                  : t("doctorDashboard.prescriptions.noMatches", "No matches found.")}
              </div>
            ) : (
              filteredRows.map((row, index) => (
                <div key={row.id || index}>
                  <button
                    type="button"
                    onClick={() => openPrescription(row)}
                    className="grid w-full grid-cols-12 items-center px-4 py-3 text-left hover:bg-[rgba(var(--clr-on-surface),0.04)]"
                  >
                    <div className="col-span-3 min-w-0">
                      <PrescriptionPatientCell
                        name={row.patientName || t("doctorDashboard.patientFallback", "Patient")}
                        subtitle={row.rxNo}
                        avatarUrl={row.patientAvatarUrl}
                      />
                    </div>
                    <div className="col-span-3 truncate text-body-medium text-on-surface-var">
                      {row.condition || t("doctorDashboard.patientsAppointments.noDiagnosis", "No diagnosis recorded")}
                    </div>
                    <div className="col-span-2 text-body-medium text-on-surface-var">{fmtShortDate(row.issuedAt, languageCode, localeCode)}</div>
                    <div className="col-span-2 text-body-medium text-on-surface-var">{fmtShortDate(row.expiresAt, languageCode, localeCode)}</div>
                    <div className="col-span-1 text-body-medium text-on-surface-var">{row.refill}</div>
                    <div className="col-span-1 flex justify-end">
                      <RxStatusPill status={row.status} />
                    </div>
                  </button>
                  {index !== filteredRows.length - 1 ? <div className="h-px bg-[rgba(var(--clr-outline-variant),0.55)]" /> : null}
                </div>
              ))
            )}
          </div>

          <div className="md:hidden mt-4 px-4 pb-4">
            {loading ? (
                <Card className="px-4 py-4 text-body-medium text-on-surface-var">
                  {t("doctorDashboard.prescriptions.loading", "Loading prescriptions...")}
                </Card>
              ) : error ? (
              <Card className="px-4 py-4 text-body-medium text-on-surface-var">
                {error}
              </Card>
            ) : filteredRows.length === 0 ? (
              <Card className="px-4 py-4 text-body-medium text-on-surface-var">
                {rows.length === 0
                  ? t("doctorDashboard.prescriptions.empty", "No prescriptions yet.")
                  : t("doctorDashboard.prescriptions.noMatches", "No matches found.")}
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredRows.map((row) => (
                  <Card
                    as="button"
                    key={row.id}
                    type="button"
                    onClick={() => openPrescription(row)}
                    className="w-full p-3 text-left hover:bg-[rgba(var(--clr-on-surface),0.04)]"
                  >
                    <PrescriptionPatientCell
                      name={row.patientName || t("doctorDashboard.patientFallback", "Patient")}
                      subtitle={row.rxNo}
                      avatarUrl={row.patientAvatarUrl}
                    />
                    <div className="mt-3 text-body-medium text-on-surface-var truncate">
                      {row.condition || t("doctorDashboard.patientsAppointments.noDiagnosis", "No diagnosis recorded")}
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
                      <div>
                        <div className="text-label-small text-on-surface-var">{t("doctorDashboard.prescriptions.columns.issued", "Issued")}</div>
                        <div className="mt-1 text-body-medium text-on-surface">{fmtShortDate(row.issuedAt, languageCode, localeCode)}</div>
                      </div>
                      <div>
                        <div className="text-label-small text-on-surface-var">{t("doctorDashboard.prescriptions.columns.expires", "Expires")}</div>
                        <div className="mt-1 text-body-medium text-on-surface">{fmtShortDate(row.expiresAt, languageCode, localeCode)}</div>
                      </div>
                      <div>
                        <div className="text-label-small text-on-surface-var">{t("doctorDashboard.prescriptionsPage.columns.refill", "Refill")}</div>
                        <div className="mt-1 text-body-medium text-on-surface">{row.refill}</div>
                      </div>
                      <div>
                        <div className="text-label-small text-on-surface-var">{t("doctorDashboard.prescriptions.columns.status", "Status")}</div>
                        <div className="mt-1">
                          <RxStatusPill status={row.status} />
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>
    </LayoutShell>
  );
}
